/** Hooks e utilidades das telas de estoque e produção. */
import type { DocStatus, Product, Warehouse } from "@cafegestor/shared";
import { formatQuantity } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiFetch, newIdempotencyKey } from "@/lib/api";
import { notifySuccess } from "@/lib/notice";

export function useWarehouses() {
  return useQuery({
    queryKey: ["estoque", "depositos"],
    queryFn: () => apiFetch<Warehouse[]>("/estoque/depositos"),
    staleTime: 30_000,
  });
}

/** Produto com unidades válidas (base + embalagens ativas). */
export function useProductDetail(productId: string) {
  return useQuery({
    queryKey: ["cadastros", "produtos", "detalhe", productId],
    queryFn: () => apiFetch<Product>(`/cadastros/produtos/${productId}`),
    enabled: productId !== "",
    staleTime: 30_000,
  });
}

export type UnitOption = { id: string; code: string; factor: string };
export function unitsOf(p: Product | undefined): UnitOption[] {
  if (!p) return [];
  return [
    { id: p.baseUnitId, code: p.baseUnitCode, factor: "1" },
    ...(p.packagings ?? [])
      .filter((x) => x.status === "active")
      .map((x) => ({ id: x.unitId, code: x.unitCode, factor: x.factor })),
  ];
}

/**
 * Ação de documento com chave de idempotência por intenção
 * (confirmar/estornar/criar: clique duplo não repete a operação).
 */
export function useDocAction<TBody, TResult>(opts: {
  path: (body: TBody) => string;
  method?: "POST" | "PUT" | "DELETE";
  invalidate: string[][];
  onDone?: (r: TResult) => void;
  /** aviso depois que a API confirmou */
  successMessage?: (r: TResult) => string;
}) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState(newIdempotencyKey);
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<TResult>(opts.path(body), {
        method: opts.method ?? "POST",
        body,
        ...(opts.method === undefined || opts.method === "POST" ? { idempotencyKey: key } : {}),
      }),
    onSuccess: async (r) => {
      setKey(newIdempotencyKey());
      for (const k of opts.invalidate) await queryClient.invalidateQueries({ queryKey: k });
      if (opts.successMessage) notifySuccess(opts.successMessage(r));
      opts.onDone?.(r);
    },
  });
}

/** Custo por unidade: R$ com até 4 casas (custo de insumo costuma ter centavos fracionados). */
export const unitCost = (v: string | null | undefined) =>
  v === null || v === undefined ? "—" : `R$ ${formatQuantity(v, 4)}`;

export const qty = (v: string | null | undefined, unit?: string) =>
  v === null || v === undefined ? "—" : `${formatQuantity(v)}${unit ? ` ${unit}` : ""}`;

export const dateBR = (v: string | null | undefined) =>
  v ? new Date(v.length === 10 ? `${v}T12:00` : v).toLocaleDateString("pt-BR") : "—";

export const dateTimeBR = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

export const today = () => new Date().toLocaleDateString("en-CA");

export const DOC_TONE: Record<DocStatus, "neutral" | "ok" | "warn" | "error"> = {
  rascunho: "warn",
  confirmado: "ok",
  cancelado: "neutral",
  estornado: "error",
};
